import type { FormEvent, ReactNode } from 'react'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { safeSessionGet, safeSessionSet } from '../lib/browserStorage'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'

const STORAGE_KEY = 'closedrecord:site-access'

function expectedPassword(): string {
  const fromEnv = import.meta.env.VITE_SITE_PASSWORD
  return typeof fromEnv === 'string' && fromEnv.length > 0 ? fromEnv : '2003'
}

function readUnlocked(): boolean {
  return safeSessionGet(STORAGE_KEY) === '1'
}

export function SitePasswordGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const isEmbed = location.pathname.startsWith('/embed/')
  const [unlocked, setUnlocked] = useState(() => isEmbed || readUnlocked())
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  if (isEmbed || unlocked) return children

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const next = password.trim()
    if (next === expectedPassword()) {
      safeSessionSet(STORAGE_KEY, '1')
      setUnlocked(true)
      setError(false)
      return
    }
    setError(true)
    setPassword('')
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#1D1D86] px-4 text-white">
      <div className="w-full max-w-sm border border-white/20 bg-white/10 p-6 backdrop-blur-xl">
        <p className="font-interactive text-[0.65rem] font-black uppercase tracking-[0.28em] text-[#FF2D78]">ClosedRecord</p>
        <h1 className="mt-3 font-display-condensed text-2xl font-bold text-white">Index locked</h1>
        <p className="mt-2 font-body text-sm text-white/80">Enter the desk password to continue.</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="site-password" className="font-interactive text-xs font-bold uppercase tracking-wide text-white">
              Password
            </Label>
            <Input
              id="site-password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
                setError(false)
              }}
              className="border-white/25 bg-white/10 text-white placeholder:text-white/40"
              placeholder="••••"
            />
            {error ? <p className="font-body text-xs text-[#FF8C00]">That did not match. Try again.</p> : null}
          </div>
          <Button type="submit" className="w-full font-interactive font-bold uppercase tracking-wide">
            Unlock
          </Button>
        </form>
      </div>
    </div>
  )
}
